/**
 * Pure correction-overlay logic for the data views (chart + table + picker).
 *
 * Corrections live in an append-only ledger and never mutate the stored series
 * (approving one does NOT recompute figures) — so the data views join them onto
 * the DB points client-side, display-only. This module owns all of that
 * matching/precedence logic so the SeriesTable, the chart input, CSV export,
 * and the corrections picker can't drift from each other. Kept React-free and
 * unit-testable (the `correctionPick.ts` precedent).
 *
 * Figure discipline: `before_value`/`after_value` pass through as the canonical
 * decimal strings the backend serialized — never `Number()`'d (locked decision
 * #6). Epoch math is used ONLY for time matching, never for figures.
 */
import type { CorrectionRecord } from "../../api/types";
import { enumerateDays, isoToGasDay } from "../../lib/dateRange";
import { formatPacificDisplay } from "../../lib/datetime";

/**
 * The data views' corrections filter: raw DB values only, DB + approved
 * corrections (the effective figures), or everything including pending
 * proposals (display-only yellow).
 */
export type CorrectionDisplayMode = "db" | "approved" | "all";

/** URL-param decoder for `?corr=` — anything unrecognized falls back to "all". */
export function parseCorrMode(raw: string | null): CorrectionDisplayMode {
  return raw === "db" || raw === "approved" ? raw : "all";
}

export type ParsedSourceId =
  | { kind: "series"; facility: string; role: string }
  | { kind: "upload_row"; uploadType: string; rowNumber: number }
  | { kind: "opaque" };

/**
 * Classify a correction's `source_identifier`. Series picks are
 * `{facility}:{role}` (facility identifiers contain dots, never colons — split
 * on the FIRST colon), upload-row picks are `{upload_type}:row-N`, and anything
 * else (hand-typed, e.g. "ME-001") is opaque and can't map to a series cell.
 */
export function parseSourceIdentifier(sourceId: string | null): ParsedSourceId {
  if (!sourceId) return { kind: "opaque" };
  const idx = sourceId.indexOf(":");
  if (idx <= 0 || idx === sourceId.length - 1) return { kind: "opaque" };
  const left = sourceId.slice(0, idx);
  const right = sourceId.slice(idx + 1);
  const rowMatch = /^row-(\d+)$/.exec(right);
  if (rowMatch) {
    return { kind: "upload_row", uploadType: left, rowNumber: Number(rowMatch[1]) };
  }
  return { kind: "series", facility: left, role: right };
}

/**
 * The overlay's series key — `{facility}:{role}`, matching how the picker built
 * the correction's `source_identifier` (`pickFromSeries`). Join on the series
 * RESPONSE's `facility_identifier`, not the selection's (hub selections carry
 * `facility: null` but the response — and the correction — carry the real id).
 */
export function overlaySeriesKey(facilityIdentifier: string, role: string): string {
  return `${facilityIdentifier}:${role}`;
}

const epoch = (iso: string): number => Date.parse(iso);

/** PT gas-day (`YYYY-MM-DD`) of an offset-aware instant; null if unparseable. */
function ptDayOf(iso: string): string | null {
  if (Number.isNaN(epoch(iso))) return null;
  const pt = formatPacificDisplay(iso);
  return pt ? pt.slice(0, 10) : null;
}

/**
 * PT gas-days covered by the half-open `[period_start, period_end)` window —
 * BE parity with `approved_substitution_days`: a missing/at-or-before-start end
 * means the single PT day of `period_start`; otherwise the last covered day is
 * the PT day of the instant just before `period_end` (an exclusive next-boundary
 * instant, so `[D 00:00, D+1 00:00)` is one day, not two). Unparseable → `[]`.
 * Bounded by `enumerateDays`' range cap so a bad window can't spin.
 */
export function correctionGasDays(periodStart: string, periodEnd: string | null): string[] {
  const startDay = ptDayOf(periodStart);
  if (!startDay) return [];
  const startE = epoch(periodStart);
  const endE = periodEnd != null ? epoch(periodEnd) : Number.NaN;
  if (Number.isNaN(endE) || endE <= startE) return [startDay];
  const lastDay = ptDayOf(new Date(endE - 1).toISOString());
  if (!lastDay || lastDay < startDay) return [startDay];
  return enumerateDays(startDay, lastDay);
}

/** The corrections targeting one table/chart cell, folded to display state. */
export type CellCorrection = {
  /** Winning approved correction (latest `decided_at`, tie `correction_id`), or null. */
  approved: CorrectionRecord | null;
  /** How many earlier approved corrections the winner supersedes (display note). */
  supersededCount: number;
  /** Pending (`proposed`) corrections targeting this cell, newest first. */
  pending: CorrectionRecord[];
  /** The winning approved correction is an exclusion (effective value = removed). */
  excluded: boolean;
  /** `after_value` of the winning approved substitution; null when excluded/none. */
  effectiveValue: string | null;
};

export type SeriesOverlay = {
  /** bucket key (gas-day for daily; bucket-start ISO for hourly) → cell state. */
  cells: Map<string, CellCorrection>;
};

export type OverlayIndex = {
  /** `overlaySeriesKey(facility, role)` → that series' overlay. */
  bySeries: Map<string, SeriesOverlay>;
  /** In-scope corrections that can't map to a series cell (upload-row/opaque ids). */
  unmatched: CorrectionRecord[];
};

/** True when the instant renders as PT wall-clock midnight (a daily pick). */
function isPtMidnight(iso: string): boolean {
  const pt = formatPacificDisplay(iso);
  return pt != null && pt.slice(11, 16) === "00:00";
}

/**
 * Hourly cell keys a correction covers, matched by EPOCH (the BE may format
 * offsets differently than our synthetic keys): an explicit `[start, end)`
 * window matches contained buckets; a null end from a daily pick (PT-midnight
 * start) is day-scoped — a day-level substitution can't be attributed to one
 * hour, so the whole gas-day is marked; a null end from an hourly pick matches
 * only its exact bucket. No matching DB bucket → one synthetic normalized key.
 */
function hourlyCellKeys(c: CorrectionRecord, bucketKeys: string[]): string[] {
  const startE = epoch(c.period_start ?? "");
  const endE = c.period_end != null ? epoch(c.period_end) : Number.NaN;
  let keys: string[];
  if (!Number.isNaN(endE) && endE > startE) {
    keys = bucketKeys.filter((b) => {
      const e = epoch(b);
      return e >= startE && e < endE;
    });
  } else if (isPtMidnight(c.period_start ?? "")) {
    const day = ptDayOf(c.period_start ?? "");
    keys = day == null ? [] : bucketKeys.filter((b) => ptDayOf(b) === day);
  } else {
    keys = bucketKeys.filter((b) => epoch(b) === startE);
  }
  return keys.length > 0 ? keys : [new Date(startE).toISOString()];
}

type CellAccumulator = { approved: CorrectionRecord[]; pending: CorrectionRecord[] };

/** Latest decision first; ties broken by correction_id so the order is stable. */
function byDecisionRecency(a: CorrectionRecord, b: CorrectionRecord): number {
  const ae = a.decided_at != null ? epoch(a.decided_at) : 0;
  const be = b.decided_at != null ? epoch(b.decided_at) : 0;
  if (ae !== be) return be - ae;
  return a.correction_id < b.correction_id ? 1 : -1;
}

/**
 * Index window/batch corrections into per-series, per-cell display state.
 * Rejected corrections are dropped at intake (they never display); corrections
 * whose source can't map to a series cell (upload rows, hand-typed ids, missing
 * period) are collected in `unmatched` for a disclosure notice.
 *
 * `bucketKeysBySeries` (hourly only): the ACTUAL bucket keys per series key, so
 * cell keys string-match the table/chart row keys whatever offset format the
 * backend emitted.
 */
export function buildOverlayIndex(
  corrections: CorrectionRecord[],
  granularity: "daily" | "hourly",
  bucketKeysBySeries?: Map<string, string[]>,
): OverlayIndex {
  const acc = new Map<string, Map<string, CellAccumulator>>();
  const unmatched: CorrectionRecord[] = [];

  for (const c of corrections) {
    if (c.status === "rejected") continue;
    const parsed = parseSourceIdentifier(c.source_identifier);
    if (parsed.kind !== "series" || c.period_start == null || Number.isNaN(epoch(c.period_start))) {
      unmatched.push(c);
      continue;
    }
    const seriesKey = overlaySeriesKey(parsed.facility, parsed.role);
    const cellKeys =
      granularity === "hourly"
        ? hourlyCellKeys(c, bucketKeysBySeries?.get(seriesKey) ?? [])
        : correctionGasDays(c.period_start, c.period_end);
    if (cellKeys.length === 0) {
      unmatched.push(c);
      continue;
    }
    let cells = acc.get(seriesKey);
    if (!cells) {
      cells = new Map();
      acc.set(seriesKey, cells);
    }
    for (const key of cellKeys) {
      let cell = cells.get(key);
      if (!cell) {
        cell = { approved: [], pending: [] };
        cells.set(key, cell);
      }
      (c.status === "approved" ? cell.approved : cell.pending).push(c);
    }
  }

  const bySeries = new Map<string, SeriesOverlay>();
  for (const [seriesKey, cells] of acc) {
    const folded = new Map<string, CellCorrection>();
    for (const [key, cell] of cells) {
      cell.approved.sort(byDecisionRecency);
      cell.pending.sort((a, b) => epoch(b.proposed_at) - epoch(a.proposed_at));
      const winner = cell.approved[0] ?? null;
      const excluded = winner?.correction_type === "exclusion";
      folded.set(key, {
        approved: winner,
        supersededCount: Math.max(cell.approved.length - 1, 0),
        pending: cell.pending,
        excluded,
        effectiveValue:
          winner != null && winner.correction_type === "substitution" ? winner.after_value : null,
      });
    }
    bySeries.set(seriesKey, { cells: folded });
  }
  return { bySeries, unmatched };
}

/** The point shape shared with `series-data.ts` (kept structural to avoid a cycle). */
type OverlayPoint = { day: string; value: string | null; n_readings?: number | null };

/**
 * Approved-only merge for the chart and CSV export. `"db"` mode is the
 * identity. An approved substitution replaces the cell's value with
 * `after_value`; an approved exclusion blanks it (a gap — the chart's
 * `connectNulls={false}` renders the hole); an approved substitution on a
 * bucket with NO db point is ADDED as a synthetic point. Pending proposals are
 * NEVER applied here — even in `"all"` mode they must not reach a plotted or
 * exported artifact; yellow is a table-only disclosure.
 */
export function applyApprovedOverlay<P extends OverlayPoint>(
  points: P[],
  overlay: SeriesOverlay | undefined,
  mode: CorrectionDisplayMode,
): (P | OverlayPoint)[] {
  if (mode === "db" || overlay == null) return points;
  const seen = new Set<string>();
  const merged: (P | OverlayPoint)[] = points.map((p) => {
    seen.add(p.day);
    const cell = overlay.cells.get(p.day);
    if (cell?.approved == null) return p;
    if (cell.excluded) return { ...p, value: null, n_readings: null };
    return { ...p, value: cell.effectiveValue };
  });
  let added = false;
  for (const [key, cell] of overlay.cells) {
    if (seen.has(key) || cell.approved == null || cell.excluded) continue;
    if (cell.effectiveValue == null) continue;
    merged.push({ day: key, value: cell.effectiveValue, n_readings: null });
    added = true;
  }
  if (added) merged.sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  return merged;
}

/** The slice of a loaded series result the page-level join needs. */
export type SeriesResultLike = {
  /** The `ChartSeriesInput.id` the page assigned (selectionKey format). */
  id: string;
  /** The RESPONSE's facility identifier — hub selections carry `facility: null`. */
  facilityIdentifier: string;
  role: string;
  /** Raw `bucket_start` keys, for hourly cell-key matching. */
  bucketKeys: string[];
};

/**
 * Page-level join shared by the batch Data tab and the Data Explorer (one
 * implementation so the two surfaces can't drift): index the corrections, then
 * re-key each series' overlay by the page's `ChartSeriesInput.id` for the
 * table/CSV. `index` is null when there are no corrections at all.
 */
export function buildPageOverlays(
  corrections: CorrectionRecord[],
  granularity: "daily" | "hourly",
  seriesResults: SeriesResultLike[],
): { index: OverlayIndex | null; overlaysById: Map<string, SeriesOverlay> } {
  if (corrections.length === 0) return { index: null, overlaysById: new Map() };
  const bucketKeys =
    granularity === "hourly"
      ? new Map(
          seriesResults.map((r) => [overlaySeriesKey(r.facilityIdentifier, r.role), r.bucketKeys]),
        )
      : undefined;
  const index = buildOverlayIndex(corrections, granularity, bucketKeys);
  const overlaysById = new Map<string, SeriesOverlay>();
  for (const r of seriesResults) {
    const overlay = index.bySeries.get(overlaySeriesKey(r.facilityIdentifier, r.role));
    if (overlay) overlaysById.set(r.id, overlay);
  }
  return { index, overlaysById };
}

/** What one table cell should render under the active display mode. */
export type CellDisplay = {
  /** The value the cell shows (effective in approved/all; the db value in db mode). */
  value: string | null;
  /** The original db value, for strikethrough/tooltip context. */
  dbValue: string | null;
  /** Drives the red/yellow styling + icon; null = plain db cell. */
  marker: "approved" | "pending" | null;
  /** Render the value struck through (an APPROVED exclusion removed it). */
  excluded: boolean;
  /** Human summary for the cell tooltip. */
  tooltip: string | null;
};

const day = (iso: string | null | undefined): string => (iso ? isoToGasDay(iso) : "");

/**
 * Resolve one cell's display under the active mode. Precedence: an approved
 * correction styles red and shows the effective value; pending proposals style
 * yellow (mode `"all"` only) but keep showing the DB value — a proposal never
 * changes the effective figure; rejected never got indexed.
 */
export function resolveCellDisplay(
  dbValue: string | null,
  cell: CellCorrection | undefined,
  mode: CorrectionDisplayMode,
): CellDisplay {
  const plain: CellDisplay = {
    value: dbValue,
    dbValue,
    marker: null,
    excluded: false,
    tooltip: null,
  };
  if (mode === "db" || cell == null) return plain;

  const notes: string[] = [];
  if (cell.approved != null) {
    const c = cell.approved;
    const reason = c.proposal_reason ? ` · reason: ${c.proposal_reason}` : "";
    if (cell.excluded) {
      notes.push(`Excluded by approved correction ${day(c.decided_at)}${reason}`);
    } else {
      notes.push(
        `Substituted by approved correction ${day(c.decided_at)} (was ${dbValue ?? "no data"})${reason}`,
      );
    }
    if (cell.supersededCount > 0) {
      notes.push(`supersedes ${cell.supersededCount} earlier approved correction(s)`);
    }
  }
  const showPending = mode === "all" && cell.pending.length > 0;
  if (showPending) {
    for (const p of cell.pending) {
      const proposal = p.correction_type === "exclusion" ? "exclusion" : (p.after_value ?? "?");
      notes.push(`Pending proposal: ${proposal} (proposed ${day(p.proposed_at)}) — not applied`);
    }
  }

  if (cell.approved != null) {
    return {
      value: cell.excluded ? dbValue : cell.effectiveValue,
      dbValue,
      marker: "approved",
      excluded: cell.excluded,
      tooltip: notes.join(" · "),
    };
  }
  if (showPending) {
    return {
      value: dbValue,
      dbValue,
      marker: "pending",
      excluded: false,
      tooltip: notes.join(" · "),
    };
  }
  return plain;
}
